import {
  type DropdownProps,
  Dropdown,
  MixpanelEventSource,
  MixpanelSourceProvider,
  POPPER_HEADER_HEIGHT,
  Stack,
} from '@talos/kyoko';
import { useEffect, useRef } from 'react';
import { MarketSelectorBlotter } from './blotter/MarketSelectorBlotter';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorWindow } from './MarketSelectorWindow';
import { MarketSelectorPresetsList } from './presets/MarketSelectorPresetsList';
import { CUSTOM_VALUE } from './tokens';

export type MarketSelectorPanelProps = {
  dropdownProps: Omit<DropdownProps, 'children'>;
  /** Useful if you have multiple market selectors rendered at once to give them different testIDs. */
  testID?: string;
};

/**
 * Narrow version of the MarketSelectorPanel.
 * Preset list overlay the blotter in narrow mode.
 * Slide the preset list off screen to show the blotter.
 * */
export const NarrowMarketSelectorPanel = ({ dropdownProps, testID }: MarketSelectorPanelProps) => {
  const initRef = useRef(false);
  const { narrowModeConfig, preset, presets, advancedMode } = useMarketSelectorContext();
  const hasPresets = !!presets?.length;

  // Show the list of presets when the panel is open, if a preset is selected.
  useEffect(() => {
    if ((preset == null || preset === CUSTOM_VALUE) && initRef.current === false) {
      initRef.current = true;
      narrowModeConfig.setShowMarketSelectorBlotter(false);
    }
  }, [preset, narrowModeConfig]);

  return advancedMode ? (
    <MarketSelectorWindow
      testID={testID}
      isOpen={dropdownProps.isOpen}
      referenceElement={dropdownProps.referenceElement}
      headerProps={dropdownProps.headerProps}
    />
  ) : (
    <Dropdown portalize isParentDropdown {...dropdownProps}>
      <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
        <Stack h="100%">
          <MarketSelectorBlotter flex="1" />
          {hasPresets ? (
            <MarketSelectorPresetsList
              forceShowSearch={true}
              position="absolute"
              top={POPPER_HEADER_HEIGHT}
              inset={0}
              zIndex={1}
              transform={narrowModeConfig.showMarketSelectorBlotter ? 'translateX(-100%)' : undefined}
              transition="transform 200ms ease"
            />
          ) : null}
        </Stack>
      </MixpanelSourceProvider>
    </Dropdown>
  );
};
